import { useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import useOrganizationApi from 'hooks/useOrganizationApi';
import catchCancel from 'helpers/catchCancel';
import { parseServerError, parseToastServerError } from 'helpers/errorHelpers';
import { Context } from 'components/Store';
import { getAccessToken } from 'utilities/base-auth';
import { CROPWISE_PROXY_URL } from 'utilities/apiConstants';
import { useToast } from '../components/ToastContainer';

const useCompanies = () => {
  const companiesAPI = useOrganizationApi('companies');
  const [{ organization }, dispatch] = useContext(Context);

  const [companies, setCompanies] = useState([]);

  const [loading, setLoading] = useState(true);

  const [isCreatingCompany, setIsCreatingCompany] = useState(null);

  const toast = useToast();

  const sortCompanies = useCallback(list => {
    return [...list].sort((a, b) =>
      a.name
        .trim()
        .toLowerCase()
        .localeCompare(b.name.trim().toLowerCase(), undefined, {
          numeric: true
        })
    );
  }, []);

  const loadCompanies = useCallback(() => {
    const { promise } = companiesAPI.fetch();
    promise
      .then(({ data }) => {
        if (data) setCompanies(sortCompanies(data));
      })
      .catch(catchCancel)
      .catch(parseServerError(dispatch))
      .catch(err => {
        parseServerError(dispatch)(err);
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companiesAPI, dispatch]);

  useEffect(() => loadCompanies(), [loadCompanies]);

  const createCompany = async values => {
    setIsCreatingCompany(true);
    const args = {
      headers: {
        'Content-Type': 'application/json',
        'cwf-context': JSON.stringify({
          organization_id: organization.id
        }),
        Authorization: `Bearer ${getAccessToken()}`
      }
    };
    const promise = axios.post(
      `${CROPWISE_PROXY_URL}/v1/base/company`,
      values,
      args
    );
    return promise
      .then(({ data }) => {
        setCompanies(prev => sortCompanies([...prev, data]));
        return data;
      })
      .catch(catchCancel)
      .catch(error => {
        parseToastServerError(
          {
            title: 'Unable to save new company.',
            content:
              'An error prevented your company from being saved. Please try again.',
            supportButton: true,
            timeout: 7000
          },
          toast,
          dispatch
        )(error);
      })
      .finally(() => {
        setIsCreatingCompany(false);
      });
  };

  return {
    companies,
    loading,
    isCreatingCompany,
    createCompany,
    loadCompanies
  };
};

export default useCompanies;
